/**
 * Helpers Web Push partagés (écran Préférences notifications + caméra).
 * Auth via apiFetch. Tout passe par /pos/camera/push/*.
 */
import { apiFetch } from './api';

export const pushSupported = typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Décode une clé VAPID base64url en Uint8Array pour applicationServerKey. */
function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** Le backend a-t-il des clés VAPID (push activable) ? */
export async function pushEnabledServerSide(): Promise<{ enabled: boolean; publicKey?: string }> {
  try {
    const r = await apiFetch('/pos/camera/push/vapid-public-key');
    if (!r.ok) return { enabled: false };
    return await r.json();
  } catch { return { enabled: false }; }
}

export async function getSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Demande la permission + s'abonne + enregistre côté backend. Retourne la sub. */
export async function subscribe(publicKey: string): Promise<PushSubscription> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(perm === 'denied' ? 'denied' : 'dismissed');
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8Array(publicKey) as BufferSource,
  });
  const r = await apiFetch('/pos/camera/push/subscribe', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sub),
  });
  if (!r.ok) throw new Error('subscribe failed');
  return sub;
}

export async function unsubscribe(sub: PushSubscription): Promise<void> {
  await apiFetch('/pos/camera/push/subscribe', {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  }).catch(() => {});
  await sub.unsubscribe();
}

export interface PrefsResponse { types: string[]; preferences: Record<string, boolean> }

export async function getPreferences(endpoint: string): Promise<PrefsResponse | null> {
  try {
    const r = await apiFetch(`/pos/camera/push/preferences?endpoint=${encodeURIComponent(endpoint)}`);
    return r.ok ? await r.json() : null;
  } catch { return null; }
}

export async function setPreferences(endpoint: string, preferences: Record<string, boolean>): Promise<boolean> {
  try {
    const r = await apiFetch('/pos/camera/push/preferences', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint, preferences }),
    });
    return r.ok;
  } catch { return false; }
}
